/**
 * Debounced, coalesced snapshot refetches.
 *
 * A build emits a burst of `item_built` frames; each names the same task. Refetching R3 once per
 * frame would hammer MSA for nothing, so plans are merged (`mergePlans`) inside a short window and
 * one refetch runs per window. At most ONE refetch is in flight: frames that land while it runs are
 * merged into the next window, so a slow GET can never be overtaken by an older one.
 */
import { isEmptyPlan, mergePlans, type RefetchPlan } from "./reduce";

export interface RefetchScheduler {
  schedule(plan: RefetchPlan): void;
  /** Run whatever is pending now (tests, and "refresh" buttons). */
  flush(): Promise<void>;
  cancel(): void;
}

export interface SchedulerTimers {
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
}

const realTimers: SchedulerTimers = {
  setTimer: (fn, ms) => setTimeout(fn, ms),
  clearTimer: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

export function createRefetchScheduler(
  run: (plan: RefetchPlan) => Promise<void>,
  opts: { delayMs?: number; timers?: SchedulerTimers } = {},
): RefetchScheduler {
  const delayMs = opts.delayMs ?? 200;
  const timers = opts.timers ?? realTimers;
  let pending: RefetchPlan | null = null;
  let timer: unknown = null;
  let running: Promise<void> | null = null;
  let cancelled = false;

  const arm = () => {
    if (timer !== null || running || cancelled) return;
    timer = timers.setTimer(() => {
      timer = null;
      void flush();
    }, delayMs);
  };

  async function flush(): Promise<void> {
    if (running) {
      await running;
      return;
    }
    if (timer !== null) {
      timers.clearTimer(timer);
      timer = null;
    }
    if (!pending || cancelled) return;
    const plan = pending;
    pending = null;
    running = (async () => {
      try {
        await run(plan);
      } catch (err) {
        console.warn("[lane-v2] refetch failed", err);
      }
    })();
    try {
      await running;
    } finally {
      running = null;
      if (pending) arm();
    }
  }

  return {
    schedule(plan) {
      if (cancelled || isEmptyPlan(plan)) return;
      pending = pending ? mergePlans(pending, plan) : plan;
      arm();
    },
    flush,
    cancel() {
      cancelled = true;
      pending = null;
      if (timer !== null) timers.clearTimer(timer);
      timer = null;
    },
  };
}
